import React from 'react'
import {
  ShieldCheck,
  HeartHandshake,
  Mountain,
  Zap,
  UsersRound,
  TrendingUp,
  Cpu,
  Headset,
} from 'lucide-react'

const coreValues = [
  {
    title: 'Reliable Deliveries',
    icon: ShieldCheck,
    number: '01',
    tag: 'TRUST',
    description:
      'We handle every shipment with care, accountability, and a commitment to dependable delivery.'
  },
  {
    title: 'Customer Partnership',
    icon: HeartHandshake,
    number: '02',
    tag: 'SERVICE',
    description:
      'We build lasting relationships through responsive support and logistics solutions tailored to business needs.'
  },
  {
    title: 'Operational Resilience',
    icon: Mountain,
    number: '03',
    tag: 'STRENGTH',
    description:
      'We adapt to changing routes, unexpected challenges, and complex supply chain demands to keep cargo moving.'
  },
  {
    title: 'Speed & Agility',
    icon: Zap,
    number: '04',
    tag: 'EFFICIENCY',
    description:
      'We streamline transportation and fulfillment to support faster, more flexible shipment movement.'
  },
  {
    title: 'Connected Network',
    icon: UsersRound,
    number: '05',
    tag: 'NETWORK',
    description:
      'We unite customers, fleet owners, transport partners, and teams to connect businesses across locations.'
  },
  {
    title: 'Sustainable Growth',
    icon: TrendingUp,
    number: '06',
    tag: 'PROGRESS',
    description:
      'We strengthen long-term partnerships and continually improve our logistics operations and capabilities.'
  },
  {
    title: 'Smart Logistics',
    icon: Cpu,
    number: '07',
    tag: 'TECHNOLOGY',
    description:
      'We embrace digital tools and shipment visibility to support smarter planning and informed decisions.'
  },
  {
    title: 'Customer First',
    icon: Headset,
    number: '08',
    tag: 'SUPPORT',
    description:
      'We prioritize clear communication, timely updates, and dependable assistance throughout the shipment journey.'
  }
]

const CoreValueCard = ({ item }) => {
  return (
    <article
      className='
        group relative flex h-full min-h-[180px] flex-col
        overflow-hidden rounded-xl border border-slate-200
        bg-white p-5 sm:p-6
        shadow-[0_4px_18px_rgba(33,31,87,0.035)]
        transition-all duration-300
        hover:-translate-y-1 hover:border-[#d32c30]/35
        hover:shadow-[0_14px_32px_rgba(33,31,87,0.09)]
      '
    >
      {/* Logistics route accent */}
      <div
        aria-hidden='true'
        className='
          absolute right-0 top-0 h-24 w-24
          rounded-bl-full bg-[#f5f6fb]
          transition-colors duration-300
          group-hover:bg-[#fff0f0]
        '
      />

      {/* <div className="relative z-10 flex items-center justify-between">
        <div
          className="
            flex h-12 w-12 items-center justify-center
            rounded-xl bg-[#211f57] text-white
            transition-colors duration-300
            group-hover:bg-[#d32c30]
          "
        >
          <Icon size={23} strokeWidth={1.8} />
        </div>

        <span className="text-[11px] font-bold tracking-[2px] text-slate-300">
          {item.number}
        </span>
      </div> */}

      <div className='relative z-10 mt-1'>
        <h3
          className='
            mt-1 text-[16px] font-bold tracking-tight
            text-[#211f57] transition-colors duration-300
            group-hover:text-[#d32c30]
          '
        >
          {item.title}
        </h3>

        <p className='mt-1 text-[12px] leading-[1.85] text-slate-500 sm:text-[13px]'>
          {item.description}
        </p>
      </div>

      {/* Route-inspired footer */}
      <div className='mt-2 flex items-center gap-2 pt-5'>
        <span className='h-1.5 w-1.5 rounded-full bg-[#d32c30]' />
        <span className='h-px w-8 bg-slate-300 transition-all duration-300 group-hover:w-14 group-hover:bg-[#d32c30]' />
        <span className='h-1.5 w-1.5 rounded-full border border-slate-300 transition-colors group-hover:border-[#d32c30]' />
        <span className='ml-auto text-[9px] font-semibold uppercase tracking-[1.5px] text-slate-400'>
          <span
            className='
            inline-flex items-center gap-1.5
            text-[9px] font-bold tracking-[1.8px]
            text-[#d32c30]
          '
          >
            <span className='h-1.5 w-1.5 rounded-full bg-[#d32c30]' />
            {item.tag}
          </span>
        </span>
      </div>
    </article>
  )
}

export default function SInfiniteCoreValues () {
  return (
    <section className='relative isolate overflow-hidden bg-[#f7f8fc] py-14 sm:py-16 lg:py-6'>
      {/* Background decoration */}
      <div
        aria-hidden='true'
        className='pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-[#211f57]/[0.04] blur-3xl'
      />
      <div
        aria-hidden='true'
        className='pointer-events-none absolute -bottom-32 -left-32 h-80 w-80 rounded-full bg-[#d32c30]/[0.045] blur-3xl'
      />

      <div className='relative mx-auto w-[92%] max-w-[1320px]'>
        {/* Header */}
        <div className='mx-auto mb-2 max-w-3xl text-center sm:mb-3 lg:mb-4'>
          <h2 className='whitespace-nowrap text-[22px] font-extrabold leading-tight tracking-[-0.5px] text-[#211f57] sm:text-[30px] md:text-[38px] lg:text-[44px]'>
            Moving Business.{' '}
            <span className='text-[#d32c30]'>Driven by Values.</span>
          </h2>

          <p className='mx-auto mt-2 max-w-2xl text-[13px] leading-7 text-slate-500 sm:text-[12px]'>
            From first-mile pickup to final delivery, our values guide every
            shipment, every partnership, and every mile we move.
          </p>
        </div>

        {/* Values grid */}
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4'>
          {coreValues.map(item => (
            <CoreValueCard key={item.number} item={item} />
          ))}
        </div>

      </div>
    </section>
  )
}
